import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_URL } from '../config';

function PasswordResetConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwords, setPasswords] = useState({
    new_password1: '',
    new_password2: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uid, setUid] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    // Get the key from query parameter and parse it
    const key = searchParams.get('key');

    if (!key) {
      setError('Invalid password reset link. Please request a new password reset.');
      return;
    }

    // Parse the key which is in format "uid-token"
    const parts = key.split('-');
    if (parts.length < 2) {
      setError('Invalid password reset link format. Please request a new password reset.');
      return;
    }

    // The token part might contain dashes, so we need to handle that
    // uid is the first part, token is everything after the first dash
    const parsedUid = parts[0];
    const parsedToken = parts.slice(1).join('-');

    setUid(parsedUid);
    setToken(parsedToken);
  }, [searchParams]);

  const handleChange = (e) => {
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    if (passwords.new_password1 !== passwords.new_password2) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (passwords.new_password1.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/password/reset/confirm/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid,
          token,
          new_password1: passwords.new_password1,
          new_password2: passwords.new_password2,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const errorData = await response.json();
        
        // Handle various error formats
        let errorMessage = 'Failed to reset password. Please try again.';
        
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.new_password1) {
          errorMessage = Array.isArray(errorData.new_password1) 
            ? errorData.new_password1[0] 
            : errorData.new_password1;
        } else if (errorData.new_password2) {
          errorMessage = Array.isArray(errorData.new_password2) 
            ? errorData.new_password2[0] 
            : errorData.new_password2;
        } else if (errorData.token) {
          errorMessage = 'Invalid or expired reset link. Please request a new password reset.';
        } else if (errorData.uid) {
          errorMessage = 'Invalid reset link. Please request a new password reset.';
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Password reset confirm error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <img 
                src="/assets/images/logo.svg" 
                alt="Member Hub Logo"
                className="w-12 h-12"
              />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Password reset successful!
            </h2>
            <div className="mt-4 rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">
                Your password has been successfully reset. You will be redirected to the login page in a few seconds.
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Go to login page now
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img 
              src="/assets/images/logo.svg" 
              alt="Member Hub Logo"
              className="w-12 h-12"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set new password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="new_password1" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="new_password1"
                name="new_password1"
                type="password"
                autoComplete="new-password"
                required
                value={passwords.new_password1}
                onChange={handleChange}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label htmlFor="new_password2" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input
                id="new_password2"
                name="new_password2"
                type="password"
                autoComplete="new-password"
                required
                value={passwords.new_password2}
                onChange={handleChange}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || !uid || !token}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/password-reset"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Request a new reset link
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PasswordResetConfirm;